import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { pageTransition, formatDate, formatCurrency } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { useToast } from "@/hooks/useToast";
import {
  listInvoices, createInvoice, sendInvoice, markInvoicePaid,
  getAgingReport,
} from "@/api/invoices";
import {
  createInvoiceClient,
  listInvoiceClients,
} from "@/api/invoiceClients";
import { listCurrencies, type CurrencyInfo } from "@/api/currencies";
import { listEmployees } from "@/api/employees";
import type { EmployeeListItem } from "@/types/employee";
import type { AgingReport, InvoiceClient } from "@/types/invoice";
import { PageHeader } from "@/components/layout/PageHeader";
import { ExportCsvButton } from "@/components/shared/ExportCsvButton";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { DataTable, type Column } from "@/components/shared/DataTable";
import { EmptyState } from "@/components/shared/EmptyState";
import { SavedViewsMenu } from "@/components/shared/SavedViewsMenu";
import { useSavedViews } from "@/hooks/useSavedViews";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  FileText,
  Plus,
  Send,
  DollarSign,
  AlertTriangle,
  BarChart3,
  Repeat,
  Users,
  UserPlus,
} from "lucide-react";
import type { Invoice, InvoiceStatus, LineItem } from "@/types/invoice";
import { INVOICE_STATUS_LABELS } from "@/types/invoice";
import type { PaginatedResponse } from "@/types/common";

export function InvoicesPage() {
  const { can } = usePermissions();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [data, setData] = useState<PaginatedResponse<Invoice> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");

  const savedViews = useSavedViews<{ statusFilter: string }>({
    scope: "invoices",
    filters: { statusFilter },
    defaultFilters: { statusFilter: "" },
    onApply: (f) => {
      setStatusFilter(f.statusFilter);
      setPage(1);
    },
  });
  const [showCreate, setShowCreate] = useState(false);
  const [showAging, setShowAging] = useState(false);
  const [aging, setAging] = useState<AgingReport | null>(null);
  const [agingLoading, setAgingLoading] = useState(false);

  // Create form — post-046 we collect the CLIENT (external recipient)
  // first and the assigned employee (internal owner) is optional.
  const [clientId, setClientId] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [empId, setEmpId] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [taxRate, setTaxRate] = useState("0");
  const [invNotes, setInvNotes] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { description: "", hours: 0, rate: 0, amount: 0 },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currencies, setCurrencies] = useState<CurrencyInfo[]>([]);
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [clients, setClients] = useState<InvoiceClient[]>([]);

  // Inline "+ Add new client" affordance — opens a tiny secondary
  // modal so the user doesn't have to leave the invoice form.
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({
    name: "",
    email: "",
    company: "",
  });
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [newClientError, setNewClientError] = useState<string | null>(null);

  const refreshClients = useCallback(async () => {
    try {
      const res = await listInvoiceClients({ includeArchived: false });
      setClients(res.items);
    } catch (err) {
      console.error("InvoicesPage: listInvoiceClients failed", err);
    }
  }, []);

  useEffect(() => {
    listCurrencies()
      .then((data) => setCurrencies(data.currencies))
      .catch(() => {
        /* non-critical; fall back to USD-only form */
      });
    // Backend caps per_page at 100. Load the directory so the
    // "Assigned to" picker can render a list instead of forcing UUID
    // input (which was producing silent 404s from the create route).
    listEmployees(1, 100)
      .then((res) => setEmployees(res.items))
      .catch((err) => {
        console.error("InvoicesPage: listEmployees failed", err);
      });
    refreshClients();
  }, [refreshClients]);

  // When the user picks a client from the dropdown, prefill the
  // editable email field. They can still override it for one-off
  // sends (the override is what gets persisted as ``client_email``).
  const handleClientPick = (id: string) => {
    setClientId(id);
    if (!id) {
      // "No client" — leave the email field as-is so a free-text
      // recipient still works without picking a saved client.
      return;
    }
    const c = clients.find((x) => x.id === id);
    if (c) setClientEmail(c.email);
  };

  const handleCreateNewClient = async () => {
    if (!newClient.name.trim()) {
      setNewClientError("Name is required");
      return;
    }
    if (!newClient.email.trim() || !newClient.email.includes("@")) {
      setNewClientError("A valid email is required");
      return;
    }
    setIsCreatingClient(true);
    setNewClientError(null);
    try {
      const created = await createInvoiceClient({
        name: newClient.name.trim(),
        email: newClient.email.trim(),
        company: newClient.company.trim() || undefined,
      });
      // Drop into the dropdown + autoselect.
      setClients((prev) => [created, ...prev]);
      setClientId(created.id);
      setClientEmail(created.email);
      setShowNewClient(false);
      setNewClient({ name: "", email: "", company: "" });
      addToast("success", "Client added");
    } catch (err) {
      const resp = (err as { response?: { data?: { detail?: unknown } } })
        ?.response;
      const detail = resp?.data?.detail;
      const msg =
        typeof detail === "string" ? detail : "Failed to add client";
      setNewClientError(msg);
    } finally {
      setIsCreatingClient(false);
    }
  };


  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await listInvoices(
        page, 20, undefined,
        (statusFilter as InvoiceStatus) || undefined,
      );
      setData(result);
    } catch {
      addToast("error", "Failed to load invoices");
    } finally {
      setIsLoading(false);
    }
  }, [page, statusFilter, addToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSend = async (id: string) => {
    try {
      const result = await sendInvoice(id);
      if (result.sent_to_email) {
        addToast("success", `Invoice sent to ${result.sent_to_email}`);
      } else {
        addToast(
          "success",
          "Invoice marked as sent (no recipient — add a client to send by email).",
        );
      }
      fetchData();
    } catch (err) {
      // Post-046 the backend surfaces real delivery failures with a 502
      // whose ``detail`` is an object: { error, sent_at, status }. We
      // tell the user delivery failed but acknowledge the FSM did move.
      const resp = (err as {
        response?: {
          status?: number;
          data?: { detail?: { error?: string } | string };
        };
      })?.response;
      const detail = resp?.data?.detail;
      let msg = "Failed to send invoice";
      if (typeof detail === "string") {
        msg = detail;
      } else if (detail && typeof detail === "object" && detail.error) {
        msg = `Marked sent, but email failed: ${detail.error}`;
      }
      console.error("InvoicesPage: send failed", err);
      addToast("error", msg);
      // Still refresh — if the FSM advanced, the row should reflect it.
      fetchData();
    }
  };

  const handleMarkPaid = async (id: string) => {
    try {
      await markInvoicePaid(id);
      addToast("success", "Invoice marked as paid");
      fetchData();
    } catch {
      addToast("error", "Failed to update invoice");
    }
  };

  const updateLineItem = (idx: number, field: keyof LineItem, value: string | number) => {
    setLineItems((prev) => {
      const next = [...prev];
      const item = { ...next[idx], [field]: value };
      if (field === "hours" || field === "rate") {
        item.amount = Math.round((item.hours || 0) * item.rate * 100) / 100;
      }
      next[idx] = item;
      return next;
    });
  };

  const addLineItem = () => {
    setLineItems((prev) => [...prev, { description: "", hours: 0, rate: 0, amount: 0 }]);
  };

  const removeLineItem = (idx: number) => {
    setLineItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleCreate = async () => {
    // Need at least a recipient (client OR free-text email) AND the
    // period + line items. Employee is optional post-046.
    if (!periodStart || !periodEnd || lineItems.length === 0) return;
    if (!clientId && !clientEmail.trim()) {
      addToast(
        "error",
        "Pick a client or type a recipient email before saving.",
      );
      return;
    }
    setIsSubmitting(true);
    try {
      await createInvoice({
        employee_id: empId || undefined,
        client_id: clientId || undefined,
        client_email: clientEmail.trim() || undefined,
        period_start: periodStart,
        period_end: periodEnd,
        line_items: lineItems,
        tax_rate: parseFloat(taxRate) || 0,
        due_date: dueDate || undefined,
        notes: invNotes || undefined,
        currency: currency || undefined,
      });
      addToast("success", "Invoice created");
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (err) {
      // Surface backend `detail` so schema / not-found / permission errors
      // are visible instead of a generic "Failed to create invoice".
      const resp = (err as { response?: { data?: { detail?: unknown } } })
        ?.response;
      const detail = resp?.data?.detail;
      let msg = "Failed to create invoice";
      if (Array.isArray(detail)) {
        const first = detail[0] as
          | { loc?: unknown[]; msg?: string }
          | undefined;
        if (first?.msg) {
          const field = String(first.loc?.[first.loc.length - 1] ?? "");
          msg = field ? `${field.replace(/_/g, " ")}: ${first.msg}` : first.msg;
        }
      } else if (typeof detail === "string") {
        msg = detail;
      }
      console.error("InvoicesPage: create failed", err);
      addToast("error", msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setClientId("");
    setClientEmail("");
    setEmpId("");
    setPeriodStart("");
    setPeriodEnd("");
    setDueDate("");
    setTaxRate("0");
    setInvNotes("");
    setCurrency("USD");
    setLineItems([{ description: "", hours: 0, rate: 0, amount: 0 }]);
  };

  const statusBadgeVariant = (s: InvoiceStatus) => {
    switch (s) {
      case "PAID": return "success" as const;
      case "PARTIALLY_PAID": return "warning" as const;
      case "ISSUED":
      case "SENT":
      case "VIEWED": return "info" as const;
      case "OVERDUE": return "danger" as const;
      case "VOID": return "default" as const;
      default: return "default" as const;
    }
  };

  const openAging = async () => {
    setShowAging(true);
    setAgingLoading(true);
    try {
      const report = await getAgingReport();
      setAging(report);
    } catch {
      addToast("error", "Failed to load aging report");
    } finally {
      setAgingLoading(false);
    }
  };

  const columns: Column<Invoice>[] = [
    {
      key: "number",
      header: "Invoice",
      render: (inv) => {
        // Recipient label: live client_name, then company snapshot if
        // present, then the recipient email, then a dash for pre-046
        // invoices with neither.
        const recipientLabel =
          inv.client_name ||
          inv.client_company ||
          inv.recipient_email ||
          inv.sent_to_email ||
          inv.employee_name ||
          "—";
        return (
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-accent/10 p-2">
              <FileText className="h-4 w-4 text-accent" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-text-primary truncate">
                {inv.invoice_number}
              </p>
              <p className="text-xs text-text-muted truncate">
                {recipientLabel}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      key: "client_email",
      header: "Client email",
      render: (inv) => {
        const email = inv.recipient_email || inv.client_email || inv.sent_to_email;
        return email ? (
          <span className="text-text-muted text-xs truncate inline-block max-w-[220px]">
            {email}
          </span>
        ) : (
          <span className="text-text-muted text-xs">—</span>
        );
      },
    },
    {
      key: "assigned",
      header: "Assigned to",
      render: (inv) => (
        <span className="text-text-muted text-xs">
          {inv.employee_name || "—"}
        </span>
      ),
    },
    {
      key: "period",
      header: "Period",
      render: (inv) => (
        <span className="text-text-muted text-xs">
          {formatDate(inv.period_start, "MMM d")} — {formatDate(inv.period_end, "MMM d, yyyy")}
        </span>
      ),
    },
    {
      key: "total",
      header: "Total",
      render: (inv) => {
        const showBase =
          inv.total_in_base !== undefined &&
          inv.fx_rate_to_base !== undefined &&
          Math.abs(inv.fx_rate_to_base - 1) > 1e-9;
        return (
          <div className="text-right">
            <span className="font-semibold text-text-primary">
              {formatCurrency(inv.total, inv.currency || "USD")}
            </span>
            {showBase && (
              <div className="text-[11px] text-text-muted">
                ≈ {formatCurrency(inv.total_in_base as number)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      render: (inv) => (
        <Badge variant={statusBadgeVariant(inv.status)}>
          {INVOICE_STATUS_LABELS[inv.status]}
        </Badge>
      ),
    },
    {
      key: "due",
      header: "Due Date",
      render: (inv) => {
        if (!inv.due_date) return <span className="text-xs text-text-muted">—</span>;
        const isOverdue = new Date(inv.due_date) < new Date() && inv.status !== "PAID";
        return (
          <span className={`text-xs ${isOverdue ? "text-danger font-medium" : "text-text-muted"}`}>
            {isOverdue && <AlertTriangle className="inline h-3 w-3 mr-1" />}
            {formatDate(inv.due_date)}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "",
      render: (inv) => (
        <div className="flex gap-1">
          {inv.status === "DRAFT" && can("manage_invoices") && (
            <button
              onClick={(e) => { e.stopPropagation(); handleSend(inv.id); }}
              className="p-1.5 rounded text-accent hover:bg-accent/10"
              title="Send"
            >
              <Send className="h-4 w-4" />
            </button>
          )}
          {(inv.status === "SENT" || inv.status === "VIEWED" || inv.status === "OVERDUE") &&
            can("manage_invoices") && (
            <button
              onClick={(e) => { e.stopPropagation(); handleMarkPaid(inv.id); }}
              className="p-1.5 rounded text-success hover:bg-success/10"
              title="Mark Paid"
            >
              <DollarSign className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const statusOptions = [
    { value: "", label: "All Statuses" },
    ...Object.entries(INVOICE_STATUS_LABELS).map(([v, l]) => ({ value: v, label: l })),
  ];

  const subtotal = lineItems.reduce((sum, li) => sum + (li.amount || 0), 0);
  const taxAmt = subtotal * (parseFloat(taxRate) || 0) / 100;
  const total = subtotal + taxAmt;

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Invoices"
        subtitle={`${data?.total ?? 0} invoices`}
        actions={
          can("manage_invoices") ? (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate("/invoices/clients")}
              >
                <Users className="h-4 w-4" />
                Clients
              </Button>
              <Button variant="ghost" onClick={() => navigate("/invoices/recurring")}>
                <Repeat className="h-4 w-4" />
                Recurring
              </Button>
              <Button variant="secondary" onClick={openAging}>
                <BarChart3 className="h-4 w-4" />
                Aging
              </Button>
              <ExportCsvButton resource="invoices" />
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4" />
                New Invoice
              </Button>
            </>
          ) : undefined
        }
      />

      <div className="flex gap-3 mb-6">
        <Select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          options={statusOptions}
        />
        <div className="ml-auto">
          <SavedViewsMenu
            views={savedViews.views}
            activeView={savedViews.activeView}
            hasUnsavedChanges={savedViews.hasUnsavedChanges}
            isDefault={savedViews.isDefault}
            onApply={savedViews.applyView}
            onSave={savedViews.saveView}
            onDelete={savedViews.deleteView}
            onRename={savedViews.renameView}
            onUpdateActive={savedViews.updateActiveView}
            onClear={savedViews.clear}
          />
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-border">
        <DataTable
          columns={columns}
          data={data?.items ?? []}
          keyExtractor={(inv) => inv.id}
          isLoading={isLoading}
          page={page}
          totalPages={data?.total_pages ?? 1}
          onPageChange={setPage}
          onRowClick={(inv) => navigate(`/invoices/${inv.id}`)}
          emptyState={
            statusFilter ? (
              <EmptyState
                icon={FileText}
                title="No invoices with that status"
                description="Try a different status or clear the filter."
                action={{
                  label: "Clear filter",
                  onClick: () => {
                    setStatusFilter("");
                    setPage(1);
                  },
                }}
              />
            ) : can("manage_invoices") ? (
              <EmptyState
                icon={FileText}
                title="No invoices yet"
                description="Bill a contractor or import a recurring template. Invoices you send will show up here with their payment status."
                action={{
                  label: "Create Invoice",
                  onClick: () => setShowCreate(true),
                }}
                secondaryAction={{
                  label: "Recurring templates",
                  to: "/invoices/recurring",
                }}
              />
            ) : (
              <EmptyState
                icon={FileText}
                title="No invoices to show"
                description="Your invoices will appear here once HR starts issuing them."
              />
            )
          }
        />
      </div>

      {/* Create Invoice Modal */}
      <Modal
        isOpen={showCreate}
        onClose={() => { setShowCreate(false); resetForm(); }}
        title="Create Invoice"
        size="lg"
      >
        <div className="space-y-4">
          {/* Recipient block — CLIENT picker + editable email override.
              At least one of these must be filled in for the invoice
              to be sendable. The "+ Add client" link opens a tiny
              secondary modal so the user doesn't lose their work. */}
          <div className="rounded-lg border border-border bg-surface-2/40 p-3 space-y-3">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Select
                  label="Client"
                  value={clientId}
                  onChange={(e) => handleClientPick(e.target.value)}
                  placeholder="Select a client…"
                  options={[
                    { value: "", label: "— No saved client —" },
                    ...clients.map((c) => ({
                      value: c.id,
                      label: c.company
                        ? `${c.name} (${c.company})`
                        : c.name,
                    })),
                  ]}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setNewClient({ name: "", email: "", company: "" });
                  setNewClientError(null);
                  setShowNewClient(true);
                }}
                className="mb-[2px] inline-flex items-center gap-1 rounded-lg border border-dashed border-accent/40 px-3 py-2 text-xs font-medium text-accent hover:bg-accent/5"
                title="Add a new client"
              >
                <UserPlus className="h-4 w-4" />
                Add new
              </button>
            </div>
            <Input
              label="Recipient email"
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              placeholder={
                clientId
                  ? "Override the saved email (optional)"
                  : "Type any email address — required if no client is picked"
              }
            />
            <p className="text-xs text-text-muted">
              The recipient email is what shows up in the &ldquo;To:&rdquo;
              field when you send the invoice. Picking a client auto-fills
              it; you can override for a one-off send.
            </p>
          </div>

          <Select
            label="Assigned to (optional)"
            value={empId}
            onChange={(e) => setEmpId(e.target.value)}
            placeholder="— Unassigned —"
            options={[
              { value: "", label: "— Unassigned —" },
              ...employees.map((emp) => ({
                value: emp.id,
                label: `${emp.first_name} ${emp.last_name}${
                  emp.work_email ? ` — ${emp.work_email}` : ""
                }`,
              })),
            ]}
          />

          <div className="grid grid-cols-3 gap-4">
            <Input
              label="Period Start"
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
            />
            <Input
              label="Period End"
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
            />
            <Input
              label="Due Date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>

          {/* Line Items */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Line Items
            </label>
            <div className="space-y-2">
              {lineItems.map((li, idx) => (
                <div key={idx} className="flex gap-2 items-end">
                  <Input
                    placeholder="Description"
                    value={li.description}
                    onChange={(e) => updateLineItem(idx, "description", e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    placeholder="Hours"
                    type="number"
                    value={li.hours?.toString() || ""}
                    onChange={(e) => updateLineItem(idx, "hours", parseFloat(e.target.value) || 0)}
                    className="w-20"
                  />
                  <Input
                    placeholder="Rate"
                    type="number"
                    value={li.rate?.toString() || ""}
                    onChange={(e) => updateLineItem(idx, "rate", parseFloat(e.target.value) || 0)}
                    className="w-24"
                  />
                  <span className="text-sm font-medium text-text-primary w-24 text-right pb-2">
                    {formatCurrency(li.amount, currency)}
                  </span>
                  {lineItems.length > 1 && (
                    <button
                      onClick={() => removeLineItem(idx)}
                      className="p-1 text-text-muted hover:text-danger mb-2"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={addLineItem}
              className="mt-2 text-xs text-accent hover:text-accent-600"
            >
              + Add line item
            </button>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Select
              label="Currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              options={
                currencies.length
                  ? currencies.map((c) => ({
                      value: c.code,
                      label: `${c.code} — ${c.name}`,
                    }))
                  : [{ value: "USD", label: "USD — US Dollar" }]
              }
            />
            <Input
              label="Tax Rate (%)"
              type="number"
              value={taxRate}
              onChange={(e) => setTaxRate(e.target.value)}
            />
            <div className="flex flex-col justify-end">
              <div className="text-right space-y-1">
                <p className="text-xs text-text-muted">Subtotal: {formatCurrency(subtotal, currency)}</p>
                {parseFloat(taxRate) > 0 && (
                  <p className="text-xs text-text-muted">Tax: {formatCurrency(taxAmt, currency)}</p>
                )}
                <p className="text-sm font-bold text-text-primary">Total: {formatCurrency(total, currency)}</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Notes (optional)
            </label>
            <textarea
              value={invNotes}
              onChange={(e) => setInvNotes(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50"
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => { setShowCreate(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreate} isLoading={isSubmitting}>
              Create Invoice
            </Button>
          </div>
        </div>
      </Modal>

      {/* Inline "Add new client" modal — opened from the invoice form
          so users can register a recipient without leaving the page.
          Saving here drops the new row into the dropdown + autoselects
          it so the invoice form is ready to submit. */}
      <Modal
        isOpen={showNewClient}
        onClose={() => setShowNewClient(false)}
        title="Add client"
        size="sm"
      >
        <div className="space-y-3">
          {newClientError && (
            <div className="rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger">
              {newClientError}
            </div>
          )}
          <Input
            label="Name"
            value={newClient.name}
            onChange={(e) =>
              setNewClient((c) => ({ ...c, name: e.target.value }))
            }
            placeholder="e.g. Acme Co"
            required
          />
          <Input
            label="Company (optional)"
            value={newClient.company}
            onChange={(e) =>
              setNewClient((c) => ({ ...c, company: e.target.value }))
            }
          />
          <Input
            label="Email"
            type="email"
            value={newClient.email}
            onChange={(e) =>
              setNewClient((c) => ({ ...c, email: e.target.value }))
            }
            placeholder="billing@acme.com"
            required
          />
          <p className="text-xs text-text-muted">
            Need more fields (address, phone, notes)? Open the Clients tab
            after saving to fill them in.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={() => setShowNewClient(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateNewClient}
              isLoading={isCreatingClient}
            >
              Add client
            </Button>
          </div>
        </div>
      </Modal>

      {/* Aging Report Modal */}
      <Modal
        isOpen={showAging}
        onClose={() => setShowAging(false)}
        title="A/R aging report"
        size="lg"
      >
        {agingLoading ? (
          // Shape the placeholder like the real report so the swap
          // doesn't resize the modal.
          <div className="space-y-3">
            <Skeleton className="h-4 w-40" />
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : aging ? (
          <div className="space-y-4">
            <p className="text-xs text-text-muted">
              As of {formatDate(aging.as_of)}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-text-muted border-b border-border">
                  <tr>
                    <th className="py-2 font-medium">Bucket</th>
                    <th className="py-2 font-medium text-right">Count</th>
                    <th className="py-2 font-medium text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {aging.buckets.map((b) => (
                    <tr key={b.label} className="border-b border-border last:border-0">
                      <td className="py-2 text-text-primary">{b.label}</td>
                      <td className="py-2 text-right text-text-muted">{b.count}</td>
                      <td className="py-2 text-right font-medium text-text-primary">
                        {formatCurrency(b.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="pt-3 font-semibold text-text-primary">
                      Total outstanding
                    </td>
                    <td />
                    <td className="pt-3 text-right font-semibold text-text-primary">
                      {formatCurrency(aging.total_outstanding)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        ) : (
          <p className="text-sm text-text-muted py-6 text-center">
            No data available.
          </p>
        )}
      </Modal>
    </motion.div>
  );
}
